<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiScrollable from '@/components/pos-v2/ui/PosUiScrollable.vue';
  import PosUiSegmented from '@/components/pos-v2/ui/PosUiSegmented.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import V2ReservationFloorPlanPicker from '@/components/v2/reservations/settings/V2ReservationFloorPlanPicker.vue';
  import V2ReservationsNotice from '@/components/v2/reservations/V2ReservationsNotice.vue';
  import { planTileTone } from '@/domain/reservations/reservation-tables-draft';
  import { isOnlineReservationTable } from '@/domain/reservations/reservation-tables';
  import type { TPlanTileTone } from '@/domain/reservations/reservation-tables-draft';
  import type {
    IReservationGroupArea,
    IReservationGroupTableOption,
  } from '@/domain/reservations/reservation-group-tables';

  /**
   * The floor plan the setup guide and the Ajustes tables card share: one
   * area at a time, tiles that read the draft's seats and mode, and chips
   * for the tables the plan cannot show. Tapping toggles the selection; the
   * owner of the selection decides what a tap means.
   */
  const props = defineProps<{
    areas: IReservationGroupArea[];
    unplaced: IReservationGroupTableOption[];
    activeSectorId?: number;
    selectedIds: number[];
    mode: 'seats' | 'groups';
    variant: 'guide' | 'settings';
    groupedIds?: number[];
    selectable?: (table: IReservationGroupTableOption) => boolean;
    /** No further table can be selected: unselected tiles stay visible but inert. */
    full?: boolean;
    disabled?: boolean;
    status: 'idle' | 'loading' | 'ready' | 'error';
  }>();
  const emit = defineEmits<{
    (e: 'toggle', tableId: number): void;
    (e: 'area', sectorId: number): void;
    (e: 'retry'): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const byId = computed(() => {
    const map = new Map<number, IReservationGroupTableOption>();
    for (const area of props.areas)
      for (const table of area.tables) map.set(table.id, table);
    for (const table of props.unplaced) map.set(table.id, table);
    return map;
  });
  // Only members guests can still get show the combination mark.
  const groupedIds = computed(() =>
    (props.groupedIds ?? []).filter((id) => {
      const table = byId.value.get(id);
      return !!table && isOnlineReservationTable(table) && table.capacity > 0;
    })
  );
  const groupedSet = computed(() => new Set(groupedIds.value));
  const activeArea = computed(
    () =>
      props.areas.find((area) => area.sector.id === props.activeSectorId) ??
      props.areas[0] ??
      null
  );
  const areaItems = computed(() =>
    props.areas.map((area) => ({
      id: String(area.sector.id),
      label: area.sector.name,
    }))
  );
  // The layout arrives async: before it does every table would look unplaced.
  const loading = computed(
    () => props.status === 'idle' || props.status === 'loading'
  );
  const unplacedTitle = computed(() =>
    tr(
      props.areas.length
        ? 'reservations.tables.editor.unplaced'
        : 'reservations.tables.editor.unplacedAll'
    )
  );

  function isSelected(table: IReservationGroupTableOption) {
    return props.selectedIds.includes(table.id);
  }
  function isSelectable(table: IReservationGroupTableOption) {
    return props.selectable ? props.selectable(table) : true;
  }
  /** The picker only knows ids. */
  function pickerSelectable(id: number) {
    const table = byId.value.get(id);
    return table ? isSelectable(table) : false;
  }
  function tone(table: IReservationGroupTableOption): TPlanTileTone {
    return planTileTone(table, props.mode, groupedSet.value);
  }
  function toneClass(table: IReservationGroupTableOption) {
    return `tone-${tone(table)}`;
  }
  function pickerTileClass(id: number) {
    const table = byId.value.get(id);
    return table ? toneClass(table) : '';
  }
  /** The second line of a tile: seats, or why the table is not bookable. */
  function tileLine(table: IReservationGroupTableOption): string {
    const guide = props.variant === 'guide';
    switch (tone(table)) {
      case 'seatless':
        return tr('reservations.tables.editor.tileSeatless');
      case 'internal':
        return tr(
          guide
            ? 'reservations.tables.editor.tileOff'
            : 'reservations.tables.mode.internal'
        );
      case 'none':
        return tr(
          guide
            ? 'reservations.tables.editor.tileNone'
            : 'reservations.tables.mode.none'
        );
      case 'inactive':
        return tr('reservations.tables.editor.tileInactive');
      default:
        return tr('reservations.tables.editor.tilePeople', {
          count: table.capacity,
        });
    }
  }
  function tileTitle(table: IReservationGroupTableOption): string {
    return `${table.name} · ${tileLine(table)}`;
  }
  /** The picker only knows ids: same text as the tooltip for its aria-label. */
  function pickerTileLabel(id: number): string {
    const table = byId.value.get(id);
    return table ? tileTitle(table) : '';
  }
  function toggle(table: IReservationGroupTableOption) {
    if (props.disabled || !isSelectable(table)) return;
    emit('toggle', table.id);
  }
</script>

<template>
  <div class="reservation-tables-plan">
    <div v-if="loading" class="plan-loading" aria-busy="true">
      <PosUiSkeleton class="plan-skeleton" />
    </div>
    <template v-else>
      <V2ReservationsNotice
        v-if="status === 'error'"
        class="plan-error"
        icon="alert-outline"
        :text="$t('reservations.tables.editor.loadError')"
        :action-label="$t('reservations.page.retry')"
        :disabled="disabled"
        @action="emit('retry')"
      />
      <!-- More than a few areas: the switch scrolls sideways instead of wrapping. -->
      <PosUiScrollable
        v-if="areas.length > 1"
        class="area-scroll"
        orientation="horizontal"
        hide-scrollbar="touch"
      >
        <PosUiSegmented
          class="area-switch"
          :value="String(activeArea ? activeArea.sector.id : '')"
          :items="areaItems"
          :disabled="disabled"
          :aria-label="$t('reservations.tables.editor.area')"
          @input="emit('area', Number($event))"
        />
      </PosUiScrollable>
      <V2ReservationFloorPlanPicker
        v-if="activeArea"
        :key="activeArea.sector.id"
        :sector="activeArea.sector"
        :tables="activeArea.tables"
        :selected-ids="selectedIds"
        :grouped-ids="groupedIds"
        :selectable="pickerSelectable"
        :tile-class="pickerTileClass"
        :tile-label="pickerTileLabel"
        :label="
          mode === 'seats' ? $t('reservations.settings.nav.tables') : undefined
        "
        :full="full"
        :disabled="disabled"
        @toggle="emit('toggle', $event)"
      >
        <template #tile="{ table, compact }">
          <span
            class="tile-content"
            :class="[toneClass(table), { compact }]"
            :title="tileTitle(table)"
          >
            <PosUiIcon
              v-if="isSelected(table) && !compact"
              class="tile-check"
              name="check-circle"
              :size="16"
            />
            <span class="tile-name">{{ table.name }}</span>
            <span class="tile-line">{{ tileLine(table) }}</span>
          </span>
        </template>
      </V2ReservationFloorPlanPicker>
      <div
        v-if="unplaced.length"
        class="unplaced"
        role="group"
        :aria-label="unplacedTitle"
      >
        <h3 class="unplaced-title">{{ unplacedTitle }}</h3>
        <div class="table-chips">
          <button
            v-for="table in unplaced"
            :key="table.id"
            type="button"
            class="table-chip"
            :class="[
              toneClass(table),
              {
                selected: isSelected(table),
                inert: !isSelectable(table),
                grouped: groupedSet.has(table.id),
              },
            ]"
            :disabled="disabled || (!isSelected(table) && full)"
            :aria-pressed="isSelected(table) ? 'true' : 'false'"
            :aria-disabled="isSelectable(table) ? undefined : 'true'"
            :title="tileTitle(table)"
            @click="toggle(table)"
          >
            <PosUiIcon
              :name="
                isSelected(table)
                  ? 'check-circle'
                  : groupedSet.has(table.id)
                  ? 'link-variant'
                  : 'checkbox-blank-circle-outline'
              "
              :size="18"
            />
            <span class="chip-text"
              ><span class="chip-name">{{ table.name }}</span
              ><span class="chip-line">{{ tileLine(table) }}</span></span
            >
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .reservation-tables-plan {
    @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-3;
    .plan-loading {
      @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-canvas tw-p-2;
      .plan-skeleton {
        @apply tw-h-[280px] tw-w-full tw-rounded-lg;
      }
    }
    .plan-error {
      @apply tw-mb-0;
    }
    .area-switch {
      // Long area names wrap instead of widening the row.
      :deep(.pos-ui-segmented-option) {
        @apply tw-h-auto tw-min-h-11 tw-whitespace-normal tw-px-3 tw-py-2;
      }
    }
    // Tile tones: what a tap on the tile changes (the picker draws the frame).
    :deep(.plan-table) {
      &.tone-seatless:not(.selected) {
        @apply tw-border-amber-300 tw-bg-amber-50 tw-text-amber-900;
      }
      &.tone-internal:not(.selected),
      &.tone-none:not(.selected),
      &.tone-inactive:not(.selected) {
        @apply tw-border-dashed tw-text-neutral-500;
      }
    }
    .tile-content {
      @apply tw-flex tw-h-full tw-w-full tw-min-w-0 tw-flex-col tw-items-center tw-justify-center tw-gap-0.5;
      .tile-check {
        @apply tw-absolute tw-right-1 tw-top-1 tw-text-orange-600;
      }
      .tile-name {
        @apply tw-max-w-full tw-truncate tw-px-1 tw-text-xs tw-font-semibold;
      }
      .tile-line {
        @apply tw-max-w-full tw-truncate tw-px-0.5 tw-text-[11px] tw-font-bold tw-leading-none tw-tabular-nums;
      }
      // One-cell tiles: smaller type, still name + seats.
      &.compact {
        .tile-name {
          @apply tw-px-0.5 tw-text-[10px];
        }
        .tile-line {
          @apply tw-text-[9px];
        }
      }
      &.tone-seatless {
        .tile-line {
          @apply tw-font-medium tw-text-amber-700;
        }
      }
      &.tone-internal,
      &.tone-none,
      &.tone-inactive {
        .tile-line {
          @apply tw-font-medium;
        }
      }
    }
    :deep(.plan-table.selected .tile-content .tile-line) {
      @apply tw-text-orange-800;
    }
    :deep(.plan-table.is-round .tile-content .tile-check) {
      @apply tw-right-3 tw-top-3;
    }
    .unplaced {
      @apply tw-flex tw-flex-col tw-gap-2;
    }
    .unplaced-title {
      @apply tw-m-0 tw-text-xs tw-font-medium tw-text-neutral-500;
    }
    // Sized by the container, not the viewport: the pane width varies.
    .table-chips {
      @apply tw-grid tw-gap-2;
      grid-template-columns: repeat(auto-fill, minmax(min(9.5rem, 100%), 1fr));
    }
    .table-chip {
      @apply tw-flex tw-min-h-11 tw-min-w-0 tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-3 tw-py-1.5 tw-text-left tw-text-sm tw-text-neutral-800 hover:tw-border-neutral-400 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-orange-500/40;
      .pos-ui-icon {
        @apply tw-shrink-0 tw-text-neutral-400;
      }
      .chip-text {
        @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5;
      }
      .chip-name {
        @apply tw-truncate tw-font-semibold;
      }
      .chip-line {
        @apply tw-truncate tw-text-xs tw-font-bold tw-tabular-nums tw-text-neutral-600;
      }
      &.tone-seatless {
        @apply tw-border-amber-300 tw-bg-amber-50;
        .chip-line {
          @apply tw-font-medium tw-text-amber-700;
        }
      }
      &.tone-internal,
      &.tone-none,
      &.tone-inactive {
        @apply tw-border-dashed tw-text-neutral-500;
        .chip-line {
          @apply tw-font-medium tw-text-neutral-500;
        }
      }
      &.grouped:not(.selected) {
        @apply tw-border-[3px] tw-border-double tw-border-neutral-400;
      }
      &.selected {
        @apply tw-border-solid tw-border-orange-600 tw-bg-orange-50 tw-text-orange-900;
        .pos-ui-icon {
          @apply tw-text-orange-600;
        }
        .chip-line {
          @apply tw-text-orange-800;
        }
      }
      &.inert {
        @apply tw-cursor-default tw-opacity-60 hover:tw-border-neutral-200;
      }
      &:disabled:not(.selected) {
        @apply tw-cursor-not-allowed tw-opacity-40;
      }
    }
  }
</style>
